import { Popover } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { IntegrationLogResponse } from '@monetize/types/app';
import { useCallback, useId, useMemo } from 'react';
import { MdRefresh } from 'react-icons/md';
import { Link as ReactDomLink } from 'react-router-dom';
import { ROLES, ROUTES } from '../../constants';
import { useAuth } from '../../services/auth0';
import { MFlex, MLink, MTooltip, MVStack } from '../Monetize';
import MCustomIconButton from '../Monetize/MCustomIconButton';
import { IntegrationSystemRow } from './IntegrationSystemRow';
import { IntegrationSystemSummary } from './useIntegrationInfo';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/*
 * Chakra's `isLoading` disables the button, and the browser blurs a focused
 * element it disables. The popover reads that blur to <body> as focus leaving
 * and closes itself, so a click (or Enter) on refresh would shut the popover
 * the refresh was meant to update. Spin the glyph in place instead — the
 * button stays enabled and keeps focus.
 */
const SPINNING_ICON_PROPS = {
  animation: `${spin} 0.8s linear infinite`,
} as const;

interface IntegrationInfoPopoverContentProps {
  systems: IntegrationSystemSummary[];
  recordId: string;
  isRefreshing: boolean;
  onRefresh: () => void;
  onSelectLog: (log: IntegrationLogResponse) => void;
}

export const IntegrationInfoPopoverContent = ({
  systems,
  recordId,
  isRefreshing,
  onRefresh,
  onSelectLog,
}: IntegrationInfoPopoverContentProps) => {
  const titleId = useId();
  const { currentTenantUserHasRole } = useAuth();
  const canViewIntegrationLogs = currentTenantUserHasRole(
    ROLES.SETTINGS_INTEGRATION_LOG_ROLES,
  );

  // Nothing disables the button while a refresh is in flight (see
  // SPINNING_ICON_PROPS), so drop the extra clicks here instead.
  const handleRefresh = useCallback(() => {
    if (!isRefreshing) {
      onRefresh();
    }
  }, [isRefreshing, onRefresh]);

  // Failures first — the icon row already told them something needs attention,
  // so put it under the cursor. The icon row itself keeps a stable order.
  const orderedSystems = useMemo(
    () => [
      ...systems.filter(({ hasFailure }) => hasFailure),
      ...systems.filter(({ hasFailure }) => !hasFailure),
    ],
    [systems],
  );

  return (
    <Popover.Positioner>
      {/* zag only links the title if it's in the DOM one frame after
          Popover.Root mounts. This content mounts on open, so wire the
          name up by hand. */}
      <Popover.Content width="500px" aria-labelledby={titleId}>
        <Popover.Header>
          <MFlex align="center" justify="space-between">
            <Popover.Title id={titleId}>Integrations</Popover.Title>
            <MTooltip label="Refresh" placement="bottom-end">
              <MCustomIconButton
                variant="icon"
                containerSize={6}
                btnSize={4}
                fontSize="md"
                icon={MdRefresh}
                iconProps={isRefreshing ? SPINNING_ICON_PROPS : undefined}
                aria-label="Refresh integration data"
                // The button stays enabled through the refetch (see
                // SPINNING_ICON_PROPS), so `aria-busy` is what tells assistive
                // tech a refresh is in flight.
                aria-busy={isRefreshing}
                data-testid="integration-info-refresh-btn"
                onClick={handleRefresh}
              />
            </MTooltip>
          </MFlex>
        </Popover.Header>
        <Popover.Body maxH="70vh" overflowY="auto">
          <MVStack align="stretch" gap={0}>
            {orderedSystems.map((summary) => (
              <IntegrationSystemRow
                key={summary.system}
                summary={summary}
                onSelectLog={onSelectLog}
              />
            ))}

            {canViewIntegrationLogs && (
              <MLink asChild color="tIndigo.base" mt={2} px={2}>
                <ReactDomLink
                  to={`${ROUTES.SETTINGS_INTEGRATION_LOG}?sourceId=${recordId}`}
                >
                  View all in Integration Logs
                </ReactDomLink>
              </MLink>
            )}
          </MVStack>
        </Popover.Body>
      </Popover.Content>
    </Popover.Positioner>
  );
};
